import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import {
  Calculator, BriefcaseBusiness, Cog, ArrowUpDown, Clock, BarChart2,
  Server, Shield, Info, Plus, X, Zap, Eye, Home, Percent,
  Check, ChevronRight, ChevronLeft, RefreshCw, AlertTriangle, Coins, TrendingUp, DollarSign, Target
} from 'lucide-react';
import { useStrategyForm } from './hooks/strategy-form/useStrategyForm';
import {
  StrategyFormData, PredictionModel, Strategy, Symbol, Basket,
  ExitCondition, BacktestSettingsType,
  RiskParameters, BacktestResult
} from './types';
import { RiskManagementTab } from './RiskManagementTab';
import { BacktestSettings } from './BacktestSettings';
import { ExecutionTimingSettings, ExecutionTimingSettings as ExecutionTimingSettingsComponent } from './ExecutionTimingSettings';
import ModelSelection from './ModelSelection';
import { SymbolSelection } from './SymbolSelection';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import * as alpaca from '@/services/alpaca';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { runBacktest } from '@/services/backtestingService';
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
// Animation imports completely removed to avoid React errors
import { useSafeDialog } from './use-safe-dialog';
import { cn } from '@/lib/utils';
import { ErrorBoundary } from '@/components/ui/errorBoundary';
import { deploymentService } from '@/services/deploymentService';
import { CreditService } from '@/services/creditService';
import { AccountTypeDisplay } from '@/components/auth/AccountTypeDisplay';
import { saveBacktest } from '@/services/backtestStorageService';
import { BacktestHistoryView } from './BacktestHistoryView';

// Badge component with variants
const Badge = ({ children, variant, className }: { children: React.ReactNode, variant?: string, className?: string }) => {
  const baseClassName = cn(
    "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
    variant === "success" && "bg-green-100 text-green-800",
    variant === "destructive" && "bg-red-100 text-red-800",
    variant === "secondary" && "bg-secondary text-secondary-foreground",
    variant === "outline" && "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
    className // Add the custom className at the end
  );
  
  return <span className={baseClassName}>{children}</span>;
};

interface StrategyStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

interface StepBasedStrategyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: any) => void;
  isStewieQuant?: boolean;
  initialData?: Partial<Strategy> | null;
}

/**
 * Multi-step strategy creation dialog.
 * IMPORTANT: Animations have been completely removed to prevent React errors in production.
 */
export const StepBasedStrategyDialog: React.FC<StepBasedStrategyDialogProps> = ({
  open,
  onOpenChange,
  onSubmit,
  isStewieQuant = false,
  initialData = null,
}) => {
  const { toast } = useToast();
  
  // Define steps for the workflow
  const steps: StrategyStep[] = [
    {
      id: 'basic',
      title: 'Basic Info',
      description: 'Name and describe your strategy',
      icon: <Info className="h-5 w-5" />,
    },
    {
      id: 'models-symbols',
      title: 'Models & Symbols',
      description: 'Select models and trading symbols',
      icon: <BriefcaseBusiness className="h-5 w-5" />,
    },
    {
      id: 'execution',
      title: 'Execution Settings',
      description: 'Configure order execution',
      icon: <Clock className="h-5 w-5" />,
    },
    {
      id: 'risk',
      title: 'Risk Management',
      description: 'Define risk parameters',
      icon: <Shield className="h-5 w-5" />,
    },
    {
      id: 'testing',
      title: 'Testing',
      description: 'Configure and run backtests',
      icon: <Calculator className="h-5 w-5" />,
    },
    {
      id: 'review',
      title: 'Review & Deploy',
      description: 'Review and deploy your strategy',
      icon: <Zap className="h-5 w-5" />,
    },
  ];
  
  // Safety and feature flags
  const { safeMode } = useSafeDialog();
  
  // Current step state
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const currentStep = steps[currentStepIndex];
  const progress = ((currentStepIndex + 1) / steps.length) * 100;

  // Reset step index when dialog opens
  useEffect(() => {
    if (open) {
      setCurrentStepIndex(0);
    }
  }, [open]);

  // Validation errors state
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  
  const [ibkrSettings, setIbkrSettings] = useState({
    accountId: 'U1234567',
    securityType: 'STK',
    exchange: 'SMART',
    orderType: 'MKT',
    timeInForce: 'DAY',
    currency: 'USD',
    outsideRth: false,
    allOrNone: false,
  });
  
  const [brokerAccount, setBrokerAccount] = useState<string>(ibkrSettings.accountId);
  
  useEffect(() => {
    if (open) {
      (async () => {
        try {
          // Use deploymentService to get account info instead of direct Alpaca API
          const accountInfo = await deploymentService.getAccountInfo();
          const acctNum = accountInfo.account_number || accountInfo.id || 'PA3V9ETMBKHD';
          setBrokerAccount(acctNum);
          setIbkrSettings(prev => ({ ...prev, accountId: acctNum }));
        } catch (err) {
          console.warn('Failed to fetch account info, using fallback:', err);
          // Use fallback account for paper trading
          const fallbackAccount = 'PA3V9ETMBKHD';
          setBrokerAccount(fallbackAccount);
          setIbkrSettings(prev => ({ ...prev, accountId: fallbackAccount }));
        }
      })();
    }
  }, [open]);
  
  const [isPaperMode, setIsPaperModeState] = useState(
    process.env.VITE_ALPACA_PAPER === 'true'
  );
  
  // Add state for deployment mode type
  const [deploymentMode, setDeploymentMode] = useState<'backtest' | 'paper' | 'live'>(
    initialData?.backtestSettings?.mode === 'live' ? 'live' : 
    initialData?.backtestSettings?.mode === 'paper' ? 'paper' : 
    'paper' // Default to paper as safe starting point, user can change in first step
  );
  
  // Add separate state to track user's intended deployment mode (not affected by backtest)
  const [intendedDeploymentMode, setIntendedDeploymentMode] = useState<'paper' | 'live'>(
    initialData?.backtestSettings?.mode === 'live' ? 'live' : 'paper'
  );
  
  // Note: Removed alpaca.setPaperMode call since we're using backend API routing
  // The backend handles paper/live mode switching internally
  
  // Update isPaperMode when deployment mode changes
  useEffect(() => {
    if (deploymentMode === 'live') {
      setIsPaperModeState(false);
    } else if (deploymentMode === 'paper') {
      setIsPaperModeState(true);
    }
    // For backtest mode, we'll leave the current isPaperMode setting
  }, [deploymentMode]);

  // Update backtest settings mode when deployment mode changes
  useEffect(() => {
    handleFormDataChange('backtestSettings', {
      ...(formData.backtestSettings || {}),
      mode: deploymentMode
    });
  }, [deploymentMode]);

  const {
    formData,
    symbolInput,
    symbolDirection,
    symbols,
    models,
    exitConditions,
    baskets,
    handleFormDataChange,
    handleExecutionTimingChange,
    handleRiskParamsChange,
    handleAddModel,
    handleRemoveModel,
    handleUpdateModel,
    handleAddSymbol,
    handleRemoveSymbol,
    handleBasketSelect,
    addExitCondition,
    removeExitCondition,
    updateExitCondition,
    handleAddBasket,
    handleRemoveBasket,
    handleUpdateBasket,
    handleBacktestSettingsChange,
    setSymbolInput,
    setSymbolDirection,
    handleSubmit,
  } = useStrategyForm(onSubmit, initialData, isPaperMode);

  useEffect(() => {
    if (initialData && open) {
      if ((initialData as any)?.ibkrSettings) {
         setIbkrSettings((initialData as any).ibkrSettings);
      }
    } else if (open) {
      setIbkrSettings({
        accountId: 'U1234567', securityType: 'STK', exchange: 'SMART',
        orderType: 'MKT', timeInForce: 'DAY', currency: 'USD',
        outsideRth: false, allOrNone: false,
      });
    }
  }, [initialData, open]);

  const handleIbkrSettingChange = (setting: keyof typeof ibkrSettings, value: any) => {
    setIbkrSettings(prev => ({ ...prev, [setting]: value }));
  };

  const [btResults, setBtResults] = useState<BacktestResult | null>(null);
  const [btLoading, setBtLoading] = useState<boolean>(false);
  const [btError, setBtError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState<boolean>(false);

  /**
   * Converts strategy configuration to backend-compatible JSON format and logs it
   * @param purpose 'backtest' or 'deploy' to indicate the intended use
   * @returns The formatted JSON object ready for API submission
   */
  const prepareAndLogStrategyJSON = (purpose: 'backtest' | 'deploy') => {
    // Extract necessary data from form state
    const {
      name,
      description,
      backtestSettings,
      allocatedCapital,
      riskParameters,
      executionTiming
    } = formData;

    // Create a unique strategy ID - use existing strategy ID if available to maintain consistency
    const strategyId = initialData?.id || (formData as any)?.id || `${name.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}`;

    // Handle symbol/basket conflict resolution
    const hasSymbols = symbols.length > 0;
    const hasBaskets = baskets.length > 0 && baskets.some(b => b.symbols.length > 0);
    
    let finalSymbols = symbols;
    let finalBaskets = baskets;
    
    // If both symbols and baskets exist, prioritize baskets and warn
    if (hasSymbols && hasBaskets) {
      console.warn('Strategy has both individual symbols and baskets. Prioritizing baskets and clearing individual symbols.');
      finalSymbols = []; // Clear individual symbols
    }

    // Format the data for backend API
    const strategyJSON = {
      strategy_id: strategyId,
      strategy: {
        id: strategyId,
        name,
        description,
        engine: "default",
        models: models.map(model => {
          // Base model configuration
          const modelConfig: any = {
            type: model.type,
            weight: model.weight || 1.0,
            decisionControl: model.decisionControl || "signal_generation",
          };
          
          // Add type-specific settings based on model type
          if (model.type === 'stewie_quant' && model.stewieQuantSettings) {
            modelConfig.stewieQuantSettings = model.stewieQuantSettings;
          } else if (model.type === 'lstm' && model.lstmSettings) {
            modelConfig.lstmSettings = model.lstmSettings;
          } else if (model.type === 'arima' && model.arimaSettings) {
            modelConfig.arimaSettings = model.arimaSettings;
          } else if (model.type === 'xgboost' && model.xgboostSettings) {
            modelConfig.xgboostSettings = model.xgboostSettings;
          } else if (model.type === 'rl_dqn') {
            // Map frontend 'rl_dqn' type to backend 'rl' type
            modelConfig.type = 'rl';
            // Use user's configured settings or provide sensible defaults
            modelConfig.rlSettings = model.rlDqnSettings || {
              stateSize: 10,
              actionSpace: 3,
              epsilon: 0.1,
              learningRate: 0.001,
              gamma: 0.95,
              memorySize: 1000,
              batchSize: 32,
              targetUpdate: 10
            };
          } else if (model.type === 'stewiegpt' && model.stewieGPTSettings) {
            modelConfig.stewieGPTSettings = model.stewieGPTSettings;
          } else if (model.type === 'hdk' && model.hdkSettings) {
            modelConfig.hdkSettings = model.hdkSettings;
          }
          
          return modelConfig;
        }),
        entryConditions: {},
        exitConditions: exitConditions || {},
        riskManagement: riskParameters || {},
        // Include allocated capital in strategy configuration
        allocatedCapital: allocatedCapital || "10000"
      },
      symbols: finalSymbols.map(symbol => ({
        ticker: symbol.ticker,
        direction: symbol.direction || "auto",
        weight: symbol.weight || 1.0,
        profitTarget: symbol.profitTarget,
        stopLoss: symbol.stopLoss,
        valueAtRisk: symbol.valueAtRisk
      })),
      baskets: finalBaskets.map(basket => ({
        id: basket.id,
        name: basket.name,
        profitTarget: basket.profitTarget,
        stopLoss: basket.stopLoss,
        valueAtRisk: basket.valueAtRisk,
        isAiManaged: basket.isAiManaged,
        symbols: basket.symbols.map(symbol => ({
          ticker: symbol.ticker,
          direction: symbol.direction || "auto",
          weight: symbol.weight || 1.0,
          profitTarget: symbol.profitTarget,
          stopLoss: symbol.stopLoss,
          valueAtRisk: symbol.valueAtRisk
        }))
      })),
      settings: {
        mode: purpose === 'backtest' ? "backtest" : intendedDeploymentMode,
        startDate: backtestSettings?.startDate || "",
        endDate: backtestSettings?.endDate || "",
        timeframe: backtestSettings?.timeframe || "day",
        provider: backtestSettings?.provider || "alpaca",
        environment: backtestSettings?.environment || "all",
        marketEnvironment: backtestSettings?.marketEnvironment || "all",
        initialCapital: backtestSettings?.initialCapital || allocatedCapital || "10000",
        includeCommission: backtestSettings?.includeCommission || false,
        commissionRate: (backtestSettings?.commissionRate || "0"),
        includeSlippage: backtestSettings?.includeSlippage || false,
        slippageModel: backtestSettings?.slippageModel || "fixed",
        slippageValue: (backtestSettings?.slippageValue || "0")
      },
      // Add execution timing settings for VM
      execution_timing: executionTiming || {
        signalFrequency: 'daily_close',
        monitorFrequency: '5min',
        executeRthOnly: true,
        allowPrePostMarket: false
      }
    };

    // For deploy, add deployment-specific configuration
    if (purpose === 'deploy') {
      // Add deployment config as a simple object without strict typing 
      (strategyJSON as any).deploymentConfig = {
        isActive: true,
        isPaperTrading: intendedDeploymentMode === 'paper',
        accountId: brokerAccount,
        ibkrSettings: ibkrSettings,
        notificationsEnabled: true
      };
    }

    // Pretty print to console with purpose label
    console.log(`%c Strategy JSON for ${purpose.toUpperCase()}`, 'background: #222; color: #bada55; font-size: 14px;');
    console.log(JSON.stringify(strategyJSON, null, 2));
    
    // Debug: Specifically log execution timing to verify it's included
    console.log(`%c Execution Timing Debug for ${purpose.toUpperCase()}:`, 'background: #333; color: #ffa500; font-size: 12px;');
    console.log('formData.executionTiming:', formData.executionTiming);
    console.log('strategyJSON.execution_timing:', strategyJSON.execution_timing);
    
    return strategyJSON;
  };

  const handleRunBacktest = async () => {
    if (!validateCurrentStep()) return;
    
    // Ensure the deployment mode is set to 'backtest' when running a backtest
    if (deploymentMode !== 'backtest') {
      setDeploymentMode('backtest');
    }
    
    setBtLoading(true);
    setBtError(null);
    setBtResults(null);
    
    try {
      // We'll let the backtest engine determine what dates are valid based on available data
      // This allows the app to work correctly regardless of the application's timeline
      
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;
      const user = sessionData.session?.user;
      if (!user) throw new Error('Not authenticated');
      
      // IMPORTANT: Do NOT create/modify strategy records for backtesting!
      // Backtests should be completely separate from strategy management
      // Use a temporary ID for the backtest process only
      const tempStrategyId = initialData?.id || `backtest_${Date.now()}`;
      
      // Ensure the backtestSettings contains timeframe before sending
      console.log("StepBasedStrategyDialog: formData.backtestSettings being sent:", formData.backtestSettings);
      
      // Create a complete settings object with guaranteed timeframe
      const completeSettings = {
        ...formData.backtestSettings,
        timeframe: formData.backtestSettings?.timeframe || 'day',
        provider: formData.backtestSettings?.provider || 'alpaca', // Ensure provider is set
        environment: formData.backtestSettings?.environment || 'all', 
        marketEnvironment: formData.backtestSettings?.marketEnvironment || 'all',
        initialCapital: formData.backtestSettings?.initialCapital || formData.allocatedCapital || '10000',
        mode: 'backtest' as const, // Explicitly set mode to backtest with proper type
      };
      
      // Prepare and log the strategy JSON for backtest
      const backtestJSON = prepareAndLogStrategyJSON('backtest');
      
      const result = await runBacktest({
        strategy_id: tempStrategyId,
        strategy_name: formData.name,
        symbols,
        baskets,
        settings: completeSettings,
      });
      
      console.log("Received data from API (first 500 chars):", 
        JSON.stringify(result).substring(0, 500) + "..."
      );
      
      // Add extra debug info for performance metrics to identify format issues
      if (result && result.performance) {
        console.log("Performance metrics data format check:", {
          totalReturn: {
            value: result.performance.totalReturn,
            type: typeof result.performance.totalReturn
          },
          sharpeRatio: {
            value: result.performance.sharpeRatio,
            type: typeof result.performance.sharpeRatio
          },
          maxDrawdown: {
            value: result.performance.maxDrawdown,
            type: typeof result.performance.maxDrawdown
          },
          winRate: {
            value: result.performance.winRate,
            type: typeof result.performance.winRate
          }
        });
      }
      
      // Check if we have a valid result - handle both formats
      if (!result) {
        console.error('Invalid backtest result format:', result);
        throw new Error('Invalid backtest result format received from API');
      }
      
      // Handle enhanced ML server format (has results.SYMBOL) vs legacy format (has performance)
      const mlResult = result as any; // Type assertion for ML server format
      
      console.log('Checking result format:', {
        hasPerformance: !!result.performance,
        hasResults: !!mlResult.results,
        resultsKeys: mlResult.results ? Object.keys(mlResult.results) : 'none'
      });
      
      // Transform ML server format to expected frontend format if needed
      if (mlResult.results && !result.performance) {
        const symbols = Object.keys(mlResult.results);
        if (symbols.length === 0) {
          console.error('No symbols found in ML results:', mlResult.results);
          throw new Error('No symbols found in backtest results');
        }
        
        const symbol = symbols[0]; // Get first symbol (OXY)
        const symbolResults = mlResult.results[symbol];
        
        console.log('Transforming ML server results for symbol:', symbol, symbolResults);
        
        // Transform to expected format
        (result as any).performance = {
          totalReturn: (symbolResults.total_return || 0) * 100, // Convert to percentage
          annualizedReturn: (symbolResults.annualized_return || symbolResults.total_return || 0) * 100,
          maxDrawdown: Math.abs(symbolResults.max_drawdown || 0) * 100, // Make positive and percentage
          sharpeRatio: symbolResults.sharpe_ratio || 0,
          trades: symbolResults.trades || 0,
          winRate: (symbolResults.win_rate || 0) * 100, // Convert to percentage if needed
          profitFactor: symbolResults.profit_factor || 1
        };
        
        // CRITICAL: Extract detailed trades from VM server response
        const detailedTrades = symbolResults.detailed_trades || [];
        console.log('🔍 DEBUG: Extracted detailed trades from VM server:', detailedTrades);
        
        // Transform detailed trades to expected format
        (result as any).trades = detailedTrades.map((trade: any) => ({
          symbol: trade.symbol,
          entryDate: trade.entryDate,
          exitDate: trade.exitDate,
          entryPrice: trade.entryPrice,
          exitPrice: trade.exitPrice,
          quantity: trade.quantity,
          profit: trade.profit,
          profitPercent: trade.profitPercent,
          direction: trade.direction,
          // Add additional fields for compatibility
          side: trade.direction === 'long' ? 'buy' : 'sell',
          pnl: trade.profit,
          pnlPercent: trade.profitPercent
        }));
        
        console.log('🔍 DEBUG: Transformed trades for frontend:', (result as any).trades);
        
        // Add equity curve if missing
        if (!(result as any).equityCurve) {
          const startValue = 100000;
          const endValue = startValue * (1 + (symbolResults.total_return || 0));
          (result as any).equityCurve = [
            { date: completeSettings.startDate || "2025-06-02", value: startValue },
            { date: completeSettings.endDate || "2025-06-30", value: endValue }
          ];
        }
        
        console.log('Transformed ML server results to frontend format:', (result as any).performance);
      }
      
      setBtResults(result);
      
      // IMPORTANT: Save backtest results to storage for history tracking
      try {
        console.log('Saving backtest results to storage...');
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        if (!sessionError && sessionData.session?.user) {
          const backtestId = await saveBacktest(sessionData.session.user.id, {
            strategyId: null, // Don't link to strategies table for pure backtests
            name: `${formData.name} - Backtest ${new Date().toLocaleDateString()}`,
            description: `Backtest results for ${formData.name}`,
            type: 'backtest',
            status: 'completed',
            startDate: completeSettings.startDate || new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString(),
            endDate: completeSettings.endDate || new Date().toISOString(),
            settings: completeSettings,
            results: {
              performance: result.performance,
              equityCurve: result.equityCurve || [],
              trades: result.trades || [],
              openPositions: result.openPositions || []
            }
          });
          
          if (backtestId) {
            console.log('Backtest saved to storage with ID:', backtestId);
            // Store the backtest ID in the result for reference
            (result as any).backtestId = backtestId;
          } else {
            console.warn('Failed to save backtest to storage');
          }
        }
      } catch (storageError) {
        console.error('Error saving backtest to storage:', storageError);
        // Don't fail the backtest if storage fails - just log the error
      }
      
      // Display different message based on source
      const resultWithSource = result as any; // Type casting to access _source
      if (resultWithSource._source) {
        toast({
          title: "Backtest Completed (Using Mock Data)",
          description: "Your backtest has finished with simulated data and has been saved to history.",
          variant: "warning",
        });
      } else {
        toast({
          title: "Backtest Completed",
          description: "Your backtest has finished running and has been saved to history.",
        });
      }
    } catch (error) {
      console.error('Backtest error:', error);
      setBtError(error instanceof Error ? error.message : String(error));
      toast({
        title: "Backtest Failed",
        description: "There was an error running your backtest.",
        variant: "destructive",
      });
    } finally {
      setBtLoading(false);
    }
  };

  const handleStrategySubmit = async () => {
    try {
      if (!validateCurrentStep()) return;
      
      // CRITICAL: Additional validation to ensure name is not empty
      if (!formData.name || formData.name.trim() === '') {
        toast({
          title: "Strategy Name Required",
          description: "Please enter a strategy name before submitting.",
          variant: "destructive",
        });
        return;
      }
      
      console.log('Strategy submission - formData.name:', formData.name);
      console.log('Strategy submission - full formData:', formData);
      
      // Get current user for credit deduction
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.user) {
        toast({
          title: "Authentication Error",
          description: "Please log in to deploy strategies.",
          variant: "destructive",
        });
        return;
      }
      
      // Calculate strategy AUM for credit deduction
      const strategyAUM = parseFloat(formData.allocatedCapital || '0');
      
      if (isNaN(strategyAUM) || strategyAUM <= 0) {
        toast({
          title: "Invalid Strategy Capital",
          description: "Please specify a valid allocated capital amount.",
          variant: "destructive",
        });
        return;
      }

      // Check if user has sufficient credits for live/paper deployment
      if (deploymentMode !== 'backtest') {
        const creditCheck = await CreditService.checkSufficientCredits(session.user.id, strategyAUM);
        
        if (!creditCheck.sufficient) {
          toast({
            title: "Insufficient Credits",
            description: `You need ${creditCheck.required.toFixed(2)} credits but only have ${creditCheck.available.toFixed(2)}. Strategy AUM: $${strategyAUM.toLocaleString()}`,
            variant: "destructive",
          });
          return;
        }
      }
      
      // Prepare and log the strategy JSON for deployment
      const deployJSON = prepareAndLogStrategyJSON('deploy');
      
      const defaultRiskParameters: RiskParameters = {
        maxPositionSizePct: 0.05,
        maxStrategyExposurePct: 100,
        isVolWeightedRisk: true,
        allowDynamicExits: true,
        stopLossType: 'percentage',
        stopLossValue: '2',
        profitTargetType: 'percentage',
        profitTargetValue: '10',
        atrPeriod: 14,
        volatilityPeriod: 20,
        correlationThreshold: 0.7,
        zscoreThreshold: 2.0
      };
      
      const finalData = { 
        ...formData, 
        ibkrSettings,
        riskParameters: formData.riskParameters || defaultRiskParameters,
        // CRITICAL: Ensure name is included in finalData
        name: formData.name.trim()
      };
      
      console.log('Final strategy data being submitted:', finalData);
      
      // Run submit in a separate task to avoid React update loops
      setTimeout(async () => {
        try {
          await handleSubmit();
          
          // If deployment mode is not backtest, call the deployment API and deduct credits
          if (deploymentMode !== 'backtest') {
            console.log('Calling deployment API for strategy deployment...');
            
            // Prepare deployment request
            const deploymentRequest = {
              strategy_id: deployJSON.strategy_id,
              symbols: deployJSON.symbols,
              settings: {
                mode: intendedDeploymentMode,
                provider: deployJSON.settings.provider || 'alpaca',
                environment: deployJSON.settings.environment || 'all',
                marketEnvironment: deployJSON.settings.marketEnvironment || 'all',
                initialCapital: deployJSON.settings.initialCapital || '100000',
                includeCommission: deployJSON.settings.includeCommission || false,
                commissionRate: deployJSON.settings.commissionRate || '0',
                includeSlippage: deployJSON.settings.includeSlippage || false,
                slippageModel: deployJSON.settings.slippageModel || 'fixed',
                slippageValue: deployJSON.settings.slippageValue || '0',
                executionDelay: '0',
                useRealTimeData: true,
                dataUpdateInterval: '60',
                timeframe: deployJSON.settings.timeframe || 'day'
              },
              // Include execution timing for VM
              execution_timing: (deployJSON as any).execution_timing,
              strategy: deployJSON.strategy,
              baskets: deployJSON.baskets || [],
              deploymentConfig: (deployJSON as any).deploymentConfig
            };
            
            try {
              const deploymentResult = await deploymentService.deployStrategy(deploymentRequest);
              console.log('Strategy deployed successfully:', deploymentResult);
              
              // Store the deployment ID in the strategy data for linking
              if (deploymentResult.success && deploymentResult.strategy_id) {
                // Update the strategy with deployment information
                (formData as any).deploymentId = deploymentResult.strategy_id;
                (formData as any).deploymentTime = deploymentResult.deployment_time;
                (formData as any).vmDeploymentConfig = {
                  vm_strategy_id: deploymentResult.strategy_id,
                  deployment_id: deploymentResult.deployment_id,
                  deployed_at: deploymentResult.deployment_time,
                  trading_mode: deploymentResult.trading_mode,
                  vm_symbols: deploymentResult.symbols,
                  vm_execution_timing: deploymentResult.execution_timing
                };
                console.log('Added deployment info to strategy data:', (formData as any).vmDeploymentConfig);
              }
              
              // Strategy deployment was successful, now deduct credits
              const creditDeduction = await CreditService.deductCreditsForTrade({
                userId: session.user.id,
                aum: strategyAUM,
                eventType: 'strategy',
                description: `Strategy deployed in ${intendedDeploymentMode} mode`,
                strategyName: formData.name || 'Unnamed Strategy',
                metadata: {
                  strategy_id: deployJSON.strategy_id,
                  deployment_mode: intendedDeploymentMode,
                  symbols: symbols.map(s => s.symbol),
                  allocated_capital: strategyAUM,
                  deployment_result: deploymentResult
                }
              });

              if (!creditDeduction.success) {
                console.error('Credit deduction failed:', creditDeduction.error);
                toast({
                  title: "Warning: Strategy Deployed",
                  description: `Strategy was deployed successfully, but there was an issue with credit deduction: ${creditDeduction.error}`,
                  variant: "destructive",
                });
              } else {
                toast({
                  title: "Strategy Deployed Successfully",
                  description: `Strategy "${formData.name || 'Unnamed Strategy'}" has been deployed and ${creditDeduction.creditsDeducted.toFixed(2)} credits deducted.`,
                  variant: "default",
                });
              }
            } catch (deploymentError) {
              console.error('Deployment API error:', deploymentError);
              toast({
                title: "Deployment Failed",
                description: `Strategy validation failed: ${deploymentError.message}`,
                variant: "destructive",
              });
              return; // Don't close dialog on deployment failure
            }
          } else {
            toast({
              title: initialData ? "Strategy Updated" : "Strategy Created",
              description: `Strategy "${formData.name || 'Unnamed Strategy'}" has been saved.`,
            });
          }
          
          // Close dialog in a separate task to avoid React issues
          setTimeout(() => {
            onOpenChange(false);
          }, 0);
        } catch (err) {
          console.error("Error in strategy submission:", err);
          toast({
            title: "Error",
            description: "Failed to save strategy. Please try again.",
            variant: "destructive",
          });
        }
      }, 0);
    } catch (err) {
      console.error("Error in strategy submission:", err);
      toast({
        title: "Error",
        description: "Failed to save strategy. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Validate current step
  const validateCurrentStep = (): boolean => {
    const errors: Record<string, string> = {};

    switch (currentStep.id) {
      case 'basic':
        if (!formData.name || formData.name.trim() === '') {
          errors.name = 'Strategy name is required';
        }
        if (!formData.allocatedCapital || parseFloat(formData.allocatedCapital) <= 0) {
          errors.allocatedCapital = 'Strategy capital allocation must be a positive amount';
        }
        break;

      case 'models-symbols':
        if (symbols.length === 0 && (!baskets || baskets.length === 0 || !baskets.some(b => b.symbols.length > 0))) {
          errors.symbols = 'At least one symbol or basket is required';
        }
        // Prevent both symbols and baskets from being used simultaneously
        if (symbols.length > 0 && baskets && baskets.length > 0 && baskets.some(b => b.symbols.length > 0)) {
          errors.symbols = 'Cannot use both individual symbols and baskets in the same strategy. Please choose one approach.';
        }
        // Validate that at least one model is selected for ML strategies
        if (models.length === 0) {
          errors.models = 'At least one AI model is required for ML strategies. Add a model or consider using a static strategy instead.';
        }
        break;

      case 'execution':
        // Could add execution validation if needed
        break;

      case 'risk':
        // Could add risk params validation if needed
        break;

      case 'testing':
        // Could add backtest validation if needed
        break;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Navigate to next step with error handling - direct state update to prevent React Error #185
  const goToNextStep = () => {
    try {
      if (!validateCurrentStep()) return;
      
      if (currentStepIndex < steps.length - 1) {
        // Direct state update - no setTimeout to avoid React Error #185
        setCurrentStepIndex(currentStepIndex + 1);
      }
    } catch (err) {
      console.error("Error in next step navigation:", err);
      // Silently recover - the ErrorBoundary will handle display
    }
  };

  // Navigate to previous step with error handling - direct state update to prevent React Error #185
  const goToPreviousStep = () => {
    try {
      if (currentStepIndex > 0) {
        // Direct state update - no setTimeout to avoid React Error #185
        setCurrentStepIndex(currentStepIndex - 1);
      }
    } catch (err) {
      console.error("Error in previous step navigation:", err);
      // Silently recover - the ErrorBoundary will handle display
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep.id) {
      case 'basic':
        return renderBasicInfoStep();
      case 'models-symbols':
        return renderModelsSymbolsStep();
      case 'execution':
        return renderExecutionStep();
      case 'risk':
        return renderRiskManagementStep();
      case 'testing':
        return renderTestingStep();
      case 'review':
        return renderReviewStep();
      default:
        return null;
    }
  };

  // Basic info step
  const renderBasicInfoStep = () => {
    return (
      <div className="space-y-8">
        {/* Enhanced Strategy Details Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
              <Info className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Strategy Details</h3>
              <p className="text-sm text-gray-600">
                Name and describe your trading strategy
              </p>
            </div>
          </div>
          
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50/30 to-indigo-50/30 rounded-lg p-4 border border-blue-100">
              <Label htmlFor="strategy-name" className="text-sm font-medium text-gray-700">
                Strategy Name *
              </Label>
              <Input
                id="strategy-name"
                placeholder="e.g., Momentum AI Strategy"
                value={formData.name}
                onChange={(e) => handleFormDataChange('name', e.target.value)}
                className="mt-2 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
              />
              {validationErrors.name && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600 flex items-center gap-2">
                    <AlertTriangle className="h-3 w-3" />
                    {validationErrors.name}
                  </p>
                </div>
              )}
            </div>

            <div className="bg-gradient-to-r from-green-50/30 to-emerald-50/30 rounded-lg p-4 border border-green-100">
              <Label htmlFor="strategy-description" className="text-sm font-medium text-gray-700">
                Description (Optional)
              </Label>
              <Textarea
                id="strategy-description"
                placeholder="Describe your strategy's approach, target markets, and expected behavior..."
                value={formData.description}
                onChange={(e) => handleFormDataChange('description', e.target.value)}
                rows={3}
                className="mt-2 border-gray-200 focus:border-green-500 focus:ring-green-500"
              />
            </div>
          </div>
        </div>

        {/* Enhanced Capital Allocation Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
              <DollarSign className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Capital Allocation</h3>
              <p className="text-sm text-gray-600">
                Set the amount of capital to allocate to this strategy
              </p>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-emerald-50/30 to-green-50/30 rounded-lg p-4 border border-green-100">
            <Label htmlFor="allocated-capital" className="text-sm font-medium text-gray-700">
              Allocated Capital (USD) *
            </Label>
            <Input
              id="allocated-capital"
              type="number"
              placeholder="100000"
              value={formData.allocatedCapital}
              onChange={(e) => handleFormDataChange('allocatedCapital', e.target.value)}
              className="mt-2 border-gray-200 focus:border-green-500 focus:ring-green-500"
            />
            {validationErrors.allocatedCapital && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600 flex items-center gap-2">
                  <AlertTriangle className="h-3 w-3" />
                  {validationErrors.allocatedCapital}
                </p>
              </div>
            )}
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-xs text-blue-700">
                💡 <strong>Tip:</strong> Start with paper trading to test your strategy before allocating real capital
              </p>
            </div>
          </div>
        </div>

        {/* Enhanced Trading Mode Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-purple-500 to-violet-600 rounded-lg">
              <Cog className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Trading Mode</h3>
              <p className="text-sm text-gray-600">
                Choose how to deploy your strategy initially
              </p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card 
                className={`cursor-pointer border-2 transition-all duration-200 ${isPaperMode ? 'border-blue-500 bg-blue-50/50 shadow-md' : 'border-gray-200 hover:border-gray-300'}`}
                onClick={() => {
                  setIsPaperModeState(true);
                  setDeploymentMode('paper');
                  setIntendedDeploymentMode('paper');
                }}
              >
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Eye className="h-5 w-5 text-blue-500" />
                    Paper Trading
                    {isPaperMode && <Badge className="bg-blue-500 text-white text-xs">Selected</Badge>}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <p className="text-sm text-gray-600">
                    Test with real market data without risking capital
                  </p>
                </CardContent>
              </Card>
              
              <Card 
                className={`cursor-pointer border-2 transition-all duration-200 ${!isPaperMode ? 'border-orange-500 bg-orange-50/50 shadow-md' : 'border-gray-200 hover:border-gray-300'}`}
                onClick={() => {
                  setIsPaperModeState(false);
                  setDeploymentMode('live');
                  setIntendedDeploymentMode('live');
                }}
              >
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Zap className="h-5 w-5 text-orange-500" />
                    Live Trading
                    {!isPaperMode && <Badge className="bg-orange-500 text-white text-xs">Selected</Badge>}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <p className="text-sm text-gray-600">
                    Deploy with real money (requires thorough testing)
                  </p>
                </CardContent>
              </Card>
            </div>
            
            {!isPaperMode && (
              <div className="p-4 rounded-lg bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-orange-800">Live Trading Mode</p>
                    <p className="text-sm text-orange-700 mt-1">
                      This strategy will use real money. Thoroughly test your strategy with paper trading before deployment.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {isPaperMode && (
              <div className="p-4 rounded-lg bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-blue-800">Paper Trading Mode</p>
                    <p className="text-sm text-blue-700 mt-1">
                      Perfect for testing strategies risk-free. You can switch to live trading later once you're confident.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Models and symbols step
  const renderModelsSymbolsStep = () => {
    return (
      <div className="space-y-8">
        {/* Enhanced Account Type Display */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6">
          <AccountTypeDisplay variant="banner" showUpgrade={true} />
        </div>
        
        {/* Enhanced Prediction Models Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
              <BriefcaseBusiness className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Prediction Models</h3>
              <p className="text-sm text-gray-600">
                Select AI models to power your strategy. Your account type determines availability.
              </p>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-blue-50/50 to-indigo-50/50 rounded-lg p-4 border border-blue-100">
            <ModelSelection
              models={models}
              symbols={symbols}
              backtestSettings={formData.backtestSettings as any}
              onAddModel={handleAddModel}
              onRemoveModel={handleRemoveModel}
              onUpdateModel={handleUpdateModel}
            />
            
            {validationErrors.models && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  {validationErrors.models}
                </p>
              </div>
            )}
          </div>
        </div>
        
        <Separator className="bg-gradient-to-r from-transparent via-blue-200 to-transparent h-px" />
        
        {/* Enhanced Trading Symbols Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
              <TrendingUp className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Trading Symbols</h3>
              <p className="text-sm text-gray-600">
                Add individual symbols or use pre-built baskets for diversification
              </p>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-green-50/50 to-emerald-50/50 rounded-lg p-4 border border-green-100">
            <SymbolSelection
              symbols={symbols}
              baskets={baskets}
              onAddSymbol={(symbol, direction, symbolData) => {
                // Add the symbol first
                handleAddSymbol(symbol, direction, symbolData);
                
                // If this symbol has profit target or stop loss, sync to strategy-level risk parameters
                if (symbolData?.profitTarget || symbolData?.stopLoss) {
                  const updatedRiskParams = { ...formData.riskParameters };
                  
                  // Update strategy-level profit target if symbol has one
                  if (symbolData.profitTarget) {
                    updatedRiskParams.profitTargetValue = symbolData.profitTarget;
                    updatedRiskParams.profitTargetType = 'percentage';
                  }
                  
                  // Update strategy-level stop loss if symbol has one
                  if (symbolData.stopLoss) {
                    updatedRiskParams.stopLossValue = symbolData.stopLoss;
                    updatedRiskParams.stopLossType = 'percentage';
                  }
                  
                  handleRiskParamsChange(updatedRiskParams);
                }
              }}
              onRemoveSymbol={(symbolToRemove) => {
                handleRemoveSymbol(symbolToRemove);
                
                // Check if this will be the last symbol removed
                const remainingSymbols = symbols.filter(s => s.ticker !== symbolToRemove && s.symbol !== symbolToRemove);
                
                if (remainingSymbols.length === 0) {
                  // Reset to defaults when all symbols are removed
                  handleRiskParamsChange({
                    profitTargetValue: '10',
                    stopLossValue: '2',
                    profitTargetType: 'percentage',
                    stopLossType: 'percentage'
                  });
                }
                // For symbol updates (remove+add), the onAddSymbol handler will take care of syncing
              }}
              onAddBasket={handleAddBasket}
              onUpdateBasket={handleUpdateBasket}
              onRemoveBasket={handleRemoveBasket}
              onBasketSelect={(basket) => {
                // When a basket is selected, apply its profit target and stop loss parameters
                if (basket && (basket.profitTarget || basket.stopLoss)) {
                  handleRiskParamsChange({
                    profitTargetValue: basket.profitTarget || formData.riskParameters?.profitTargetValue || '5',
                    stopLossValue: basket.stopLoss || formData.riskParameters?.stopLossValue || '2'
                  });
                }
                // Then call the original handleBasketSelect
                handleBasketSelect(basket);
              }}
              symbolInput={symbolInput}
              setSymbolInput={setSymbolInput}
              symbolDirection={symbolDirection}
              setSymbolDirection={setSymbolDirection}
            />
            
            {validationErrors.symbols && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  {validationErrors.symbols}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render execution settings step
  const renderExecutionStep = () => {
    const isStaticStrategy = models.length === 0 || models.every(m => m.type === 'static');
    
    return (
      <div className="space-y-8">
        {/* Static Strategy Signal Configuration */}
        {isStaticStrategy && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
                <Zap className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Signal Generation Type</h3>
                <p className="text-sm text-gray-600">
                  Choose how your static strategy generates trading signals
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Technical Analysis Signals */}
              <Card 
                className={`cursor-pointer border-2 transition-all duration-200 ${
                  formData.staticSignalType === 'technical' 
                    ? 'border-blue-500 bg-blue-50/50 shadow-md' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => handleFormDataChange('staticSignalType', 'technical')}
              >
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart2 className="h-5 w-5 text-blue-500" />
                    Technical Analysis
                    {formData.staticSignalType === 'technical' && (
                      <Badge className="bg-blue-500 text-white text-xs">Selected</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <p className="text-sm text-gray-600">
                    Use moving averages, RSI, and momentum indicators to generate buy/sell signals
                  </p>
                  <div className="mt-2 text-xs text-green-600 font-medium">
                    ✓ Balanced buy/sell signals
                  </div>
                </CardContent>
              </Card>

              {/* Immediate Execution */}
              <Card 
                className={`cursor-pointer border-2 transition-all duration-200 ${
                  formData.staticSignalType === 'immediate' 
                    ? 'border-orange-500 bg-orange-50/50 shadow-md' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => handleFormDataChange('staticSignalType', 'immediate')}
              >
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Zap className="h-5 w-5 text-orange-500" />
                    Immediate Execution
                    {formData.staticSignalType === 'immediate' && (
                      <Badge className="bg-orange-500 text-white text-xs">Selected</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <p className="text-sm text-gray-600">
                    Execute trades immediately at best available market price
                  </p>
                  <div className="mt-2 text-xs text-orange-600 font-medium">
                    ⚡ Instant execution
                  </div>
                </CardContent>
              </Card>

              {/* Spread-Based Signals */}
              <Card 
                className={`cursor-pointer border-2 transition-all duration-200 ${
                  formData.staticSignalType === 'spread' 
                    ? 'border-purple-500 bg-purple-50/50 shadow-md' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => handleFormDataChange('staticSignalType', 'spread')}
              >
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Target className="h-5 w-5 text-purple-500" />
                    Spread-Based
                    {formData.staticSignalType === 'spread' && (
                      <Badge className="bg-purple-500 text-white text-xs">Selected</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <p className="text-sm text-gray-600">
                    Trade when bid-ask spreads are tight (good liquidity)
                  </p>
                  <div className="mt-2 text-xs text-purple-600 font-medium">
                    📊 Liquidity-based
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Signal Type Configuration */}
            {formData.staticSignalType === 'technical' && (
              <div className="bg-blue-50/30 rounded-lg p-4 border border-blue-100">
                <h4 className="font-medium text-blue-800 mb-3">Technical Analysis Settings</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="use-ma-crossover"
                        checked={formData.technicalSettings?.useMACrossover !== false}
                        onChange={(e) => handleFormDataChange('technicalSettings', {
                          ...formData.technicalSettings,
                          useMACrossover: e.target.checked
                        })}
                        className="rounded border-gray-300"
                      />
                      <label htmlFor="use-ma-crossover" className="text-sm font-medium">
                        Moving Average Crossover
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="use-rsi"
                        checked={formData.technicalSettings?.useRSI !== false}
                        onChange={(e) => handleFormDataChange('technicalSettings', {
                          ...formData.technicalSettings,
                          useRSI: e.target.checked
                        })}
                        className="rounded border-gray-300"
                      />
                      <label htmlFor="use-rsi" className="text-sm font-medium">
                        RSI Overbought/Oversold
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="use-momentum"
                        checked={formData.technicalSettings?.useMomentum === true}
                        onChange={(e) => handleFormDataChange('technicalSettings', {
                          ...formData.technicalSettings,
                          useMomentum: e.target.checked
                        })}
                        className="rounded border-gray-300"
                      />
                      <label htmlFor="use-momentum" className="text-sm font-medium">
                        Momentum Analysis
                      </label>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-700">RSI Oversold Threshold</label>
                      <Input
                        type="number"
                        min="10"
                        max="40"
                        value={formData.technicalSettings?.rsiOversold || 30}
                        onChange={(e) => handleFormDataChange('technicalSettings', {
                          ...formData.technicalSettings,
                          rsiOversold: parseInt(e.target.value)
                        })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">RSI Overbought Threshold</label>
                      <Input
                        type="number"
                        min="60"
                        max="90"
                        value={formData.technicalSettings?.rsiOverbought || 70}
                        onChange={(e) => handleFormDataChange('technicalSettings', {
                          ...formData.technicalSettings,
                          rsiOverbought: parseInt(e.target.value)
                        })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {formData.staticSignalType === 'immediate' && (
              <div className="bg-orange-50/30 rounded-lg p-4 border border-orange-100">
                <h4 className="font-medium text-orange-800 mb-3">Immediate Execution Settings</h4>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Execution Action
                    </label>
                    <select
                      value={formData.immediateAction || 'buy'}
                      onChange={(e) => handleFormDataChange('immediateAction', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:border-orange-500 focus:ring-orange-500"
                    >
                      <option value="buy">Buy Only</option>
                      <option value="sell">Sell Only</option>
                      <option value="both">Both Buy & Sell</option>
                    </select>
                  </div>
                  <div className="bg-orange-100/50 rounded-md p-3">
                    <p className="text-sm text-orange-800">
                      <strong>Warning:</strong> Immediate execution will place market orders as soon as the strategy starts. 
                      Make sure you understand the risks before using this option.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {formData.staticSignalType === 'spread' && (
              <div className="bg-purple-50/30 rounded-lg p-4 border border-purple-100">
                <h4 className="font-medium text-purple-800 mb-3">Spread-Based Settings</h4>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Maximum Spread Threshold (%)
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max="1.0"
                    value={formData.spreadThreshold || 0.2}
                    onChange={(e) => handleFormDataChange('spreadThreshold', parseFloat(e.target.value))}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    Trades will only execute when the bid-ask spread is below this threshold
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Standard Execution Timing Settings */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
              <Clock className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Execution Timing</h3>
              <p className="text-sm text-gray-600">
                Configure when and how often your strategy executes
              </p>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-blue-50/50 to-indigo-50/50 rounded-lg p-4 border border-blue-100">
            <ExecutionTimingSettingsComponent
              settings={formData.executionTiming || {
                signalFrequency: 'daily_close',
                monitorFrequency: '5min',
                executeRthOnly: true,
                allowPrePostMarket: false,
              }}
              onSettingsChange={handleExecutionTimingChange}
              selectedModels={models.map(m => m.type)}
            />
          </div>
        </div>
      </div>
    );
  };

  // Risk management step
  const renderRiskManagementStep = () => {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium mb-2">Risk Management</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Set parameters to manage risk and protect your portfolio
          </p>
          
          <RiskManagementTab
            formData={{
              ...formData,
              riskParameters: formData.riskParameters || {
                maxPositionSizePct: 0.10, // Default to 10%
                maxStrategyExposurePct: 100,
                isVolWeightedRisk: true,
                allowDynamicExits: true,
                stopLossType: 'percentage',
                stopLossValue: '2',
                profitTargetType: 'percentage',
                profitTargetValue: '10',
                atrPeriod: 14,
                volatilityPeriod: 20,
                correlationThreshold: 0.7,
                zscoreThreshold: 2.0
              }
            }}
            exitConditions={exitConditions}
            onAddExitCondition={addExitCondition}
            onUpdateExitCondition={updateExitCondition}
            onRemoveExitCondition={removeExitCondition}
            onRiskParamsChange={handleRiskParamsChange}
            onFormDataChange={handleFormDataChange}
            models={models}
          />
        </div>
      </div>
    );
  };

  // Testing step
  const renderTestingStep = () => {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium mb-2">Backtest Settings</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Configure your backtest parameters and analyze results
          </p>
          
          <BacktestSettings
            settings={formData.backtestSettings as any}
            onSettingsChange={handleBacktestSettingsChange}
          />
        </div>
        
        <div className="flex flex-col space-y-4">
          <Button
            onClick={handleRunBacktest}
            disabled={btLoading}
            variant="default"
            className="gap-2"
          >
            {btLoading ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Running Backtest...
              </>
            ) : (
              <>
                <Calculator className="h-4 w-4" />
                Run Backtest
              </>
            )}
          </Button>
          
          {btError && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-100">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{btError}</p>
              </div>
            </div>
          )}
          
          {btResults && (
            <div className="mt-6 p-4 rounded-lg bg-green-50 border border-green-200">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-medium text-green-800">Backtest Results</h4>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowHistoryDialog(true)}
                    className="gap-2"
                  >
                    <Clock className="h-4 w-4" />
                    View History
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-white p-3 rounded border">
                  <div className="text-sm text-gray-600">Total Return</div>
                  <div className={`text-xl font-bold ${
                    (btResults.performance?.totalReturn || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {formatMetric(btResults.performance?.totalReturn, true)}%
                  </div>
                </div>
                
                <div className="bg-white p-3 rounded border">
                  <div className="text-sm text-gray-600">Sharpe Ratio</div>
                  <div className="text-xl font-bold">
                    {formatMetric(btResults.performance?.sharpeRatio, false)}
                  </div>
                </div>
                
                <div className="bg-white p-3 rounded border">
                  <div className="text-sm text-gray-600">Max Drawdown</div>
                  <div className="text-xl font-bold text-red-600">
                    {formatMetric(btResults.performance?.maxDrawdown, true)}%
                  </div>
                </div>
                
                <div className="bg-white p-3 rounded border">
                  <div className="text-sm text-gray-600">Win Rate</div>
                  <div className="text-xl font-bold">
                    {formatMetric(btResults.performance?.winRate, true)}%
                  </div>
                </div>
                
                <div className="bg-white p-3 rounded border">
                  <div className="text-sm text-gray-600">Total Trades</div>
                  <div className="text-xl font-bold">
                    {btResults.performance?.trades || 0}
                  </div>
                </div>
                
                <div className="bg-white p-3 rounded border">
                  <div className="text-sm text-gray-600">Profit Factor</div>
                  <div className="text-xl font-bold">
                    {formatMetric(btResults.performance?.profitFactor, false)}
                  </div>
                </div>
              </div>
              
              {(btResults as any).backtestId && (
                <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
                  ✅ Results saved to history (ID: {(btResults as any).backtestId})
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Review step
  const renderReviewStep = () => {
    // Debug: Log execution timing in review step
    console.log('🔍 Review Step - formData.executionTiming:', formData.executionTiming);
    console.log('🔍 Review Step - Complete formData:', formData);
    
    return (
      <div className="space-y-6">
        <Card className="border-2 border-primary">
          <CardHeader className="bg-primary/5">
            <CardTitle>Strategy Summary</CardTitle>
            <CardDescription>
              Review all settings before deployment
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-lg">{formData.name}</h3>
                <p className="text-muted-foreground">
                  {formData.description || "No description provided"}
                </p>
              </div>
              
              <Separator />
              
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <h4 className="font-medium mb-2">Configuration</h4>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Deployment Mode:</span>
                      <span className="text-sm font-medium flex items-center">
                        {deploymentMode === 'backtest' ? (
                          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-secondary text-secondary-foreground flex items-center gap-1">
                            <BarChart2 className="h-3 w-3" />
                            BACKTEST
                          </span>
                        ) : intendedDeploymentMode === 'paper' ? (
                          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-secondary text-secondary-foreground flex items-center gap-1">
                            <Eye className="h-3 w-3" />
                            PAPER
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-red-100 text-red-800 flex items-center gap-1">
                            <Zap className="h-3 w-3" />
                            LIVE
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Account ID:</span>
                      <span className="text-sm font-mono">{brokerAccount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Signal Frequency:</span>
                      <span className="text-sm font-medium">
                        {formData.executionTiming?.signalFrequency || 'daily_close'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Monitor Frequency:</span>
                      <span className="text-sm font-medium">
                        {formData.executionTiming?.monitorFrequency || '5min'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Trading Hours:</span>
                      <span className="text-sm font-medium">
                        {formData.executionTiming?.executeRthOnly ? 'Regular Market Hours' : 'Extended Hours'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium mb-2">Models & Assets</h4>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Symbols:</span>
                      <span className="text-sm font-medium">{symbols.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Baskets:</span>
                      <span className="text-sm font-medium">{baskets?.length || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">AI Models:</span>
                      <span className="text-sm font-medium">{models.length}</span>
                    </div>
                    {models.length > 0 && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        <span className="font-medium">Models: </span>
                        {models.map(model => model.type).join(', ')}
                      </div>
                    )}
                    {exitConditions && Object.keys(exitConditions).length > 0 && (
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Exit Conditions:</span>
                        <span className="text-sm font-medium">{Object.keys(exitConditions).length}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <Separator />
              
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <h4 className="font-medium mb-2">Risk Management</h4>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Capital Allocation:</span>
                      <span className="text-sm font-medium">
                        ${parseFloat(formData.allocatedCapital).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Max Position Size:</span>
                      <span className="text-sm font-medium">
                        {formData.riskParameters?.maxPositionSizePct ? `${formData.riskParameters.maxPositionSizePct * 100}%` : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Stop Loss:</span>
                      <span className="text-sm font-medium">
                        {(() => {
                          // Check if we have symbol-level stop losses
                          const symbolStopLosses = symbols.map(s => s.stopLoss).filter(Boolean);
                          if (symbolStopLosses.length > 0) {
                            const uniqueStopLosses = [...new Set(symbolStopLosses)];
                            if (uniqueStopLosses.length === 1) {
                              return `${uniqueStopLosses[0]}% (per symbol)`;
                            } else {
                              return `${uniqueStopLosses.join(', ')}% (varied by symbol)`;
                            }
                          }
                          return formData.riskParameters?.stopLossValue ? `${formData.riskParameters.stopLossValue}%` : 'None';
                        })()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Take Profit:</span>
                      <span className="text-sm font-medium">
                        {(() => {
                          // Check if we have symbol-level profit targets
                          const symbolProfitTargets = symbols.map(s => s.profitTarget).filter(Boolean);
                          if (symbolProfitTargets.length > 0) {
                            const uniqueProfitTargets = [...new Set(symbolProfitTargets)];
                            if (uniqueProfitTargets.length === 1) {
                              return `${uniqueProfitTargets[0]}% (per symbol)`;
                            } else {
                              return `${uniqueProfitTargets.join(', ')}% (varied by symbol)`;
                            }
                          }
                          return formData.riskParameters?.profitTargetValue ? `${formData.riskParameters.profitTargetValue}%` : 'None';
                        })()}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium mb-2">Backtest & Settings</h4>
                  {btResults ? (
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Total Return:</span>
                        <span className="text-sm font-medium">{formatMetric(btResults.performance?.totalReturn, true)}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Sharpe Ratio:</span>
                        <span className="text-sm font-medium">{formatMetric(btResults.performance?.sharpeRatio, false)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Max Drawdown:</span>
                        <span className="text-sm font-medium text-red-600">{formatMetric(btResults.performance?.maxDrawdown, true)}%</span>
                      </div>
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Data Provider:</span>
                          <span className="text-sm font-medium">{formData.backtestSettings?.provider || 'alpaca'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Timeframe:</span>
                          <span className="text-sm font-medium">{formData.backtestSettings?.timeframe || 'day'}</span>
                        </div>
                        {formData.backtestSettings?.includeCommission && (
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Commission:</span>
                            <span className="text-sm font-medium">{formData.backtestSettings.commissionRate || '0'}%</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-muted-foreground italic">No backtest results available</p>
                      {deploymentMode !== 'backtest' && (
                        <p className="text-sm text-amber-700 mt-1">
                          It's recommended to backtest strategies before deployment.
                        </p>
                      )}
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Data Provider:</span>
                          <span className="text-sm font-medium">{formData.backtestSettings?.provider || 'alpaca'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Timeframe:</span>
                          <span className="text-sm font-medium">{formData.backtestSettings?.timeframe || 'day'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Show the Deployment Mode selection if we have backtest results, allowing users to transition to paper/live */}
        {btResults && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Ready to Deploy</h3>
            <p className="text-sm text-muted-foreground">
              Your strategy has been backtested. Choose how you would like to deploy it:
            </p>
            
            <div className="grid grid-cols-2 gap-3">
              <Card 
                className={`cursor-pointer border-2 transition-all ${intendedDeploymentMode === 'paper' ? 'border-primary bg-muted' : 'hover:border-muted-foreground'}`}
                onClick={() => {
                  setDeploymentMode('paper');
                  setIntendedDeploymentMode('paper');
                  setIsPaperModeState(true);
                }}
              >
                <CardHeader className="p-3 pb-0">
                  <CardTitle className="text-base flex justify-between items-center">
                    <Eye className="h-5 w-5 mr-1.5" />
                    Deploy to Paper Trading
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-1">
                  <p className="text-xs text-muted-foreground">
                    Test with real market data without using real money
                  </p>
                </CardContent>
              </Card>
              
              <Card 
                className={`cursor-pointer border-2 transition-all ${intendedDeploymentMode === 'live' ? 'border-primary bg-muted' : 'hover:border-muted-foreground'}`}
                onClick={() => {
                  setDeploymentMode('live');
                  setIntendedDeploymentMode('live');
                  setIsPaperModeState(false);
                }}
              >
                <CardHeader className="p-3 pb-0">
                  <CardTitle className="text-base flex justify-between items-center">
                    <Zap className="h-5 w-5 mr-1.5" />
                    Deploy to Live Trading
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-1">
                  <p className="text-xs text-muted-foreground">
                    Trade with real money on actual markets
                  </p>
                </CardContent>
              </Card>
            </div>
            
            {/* Credit Cost Display for Paper/Live deployment */}
            {deploymentMode !== 'backtest' && (
              <Card className="border-blue-200 bg-blue-50">
                <CardHeader className="p-4 pb-2">
                  <CardTitle className="text-base flex items-center text-blue-700">
                    <Coins className="h-5 w-5 mr-2" />
                    Credit Cost Estimate
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-blue-600">
                        Strategy AUM: ${parseFloat(formData.allocatedCapital || '0').toLocaleString()}
                      </p>
                      <p className="text-xs text-blue-500 mt-1">
                        Fee: 10 basis points (0.1%) of allocated capital
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-blue-700">
                        {(() => {
                          const strategyAUM = parseFloat(formData.allocatedCapital || '0');
                          if (isNaN(strategyAUM)) return '0.00';
                          const credits = CreditService.calculateCreditsRequired(strategyAUM);
                          return credits.toFixed(2);
                        })()} Credits
                      </div>
                      <p className="text-xs text-blue-500">Will be deducted upon deployment</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
        
        {/* Show warning for live trading */}
        {intendedDeploymentMode === 'live' && (
          <div className="p-4 rounded-lg bg-amber-50 border border-amber-100">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-amber-800">Live Trading Warning</p>
                <p className="text-sm text-amber-700 mt-1">
                  You are about to deploy a strategy using real money. Make sure all settings are correct and risks are understood before proceeding.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Show recommendation for backtest if not run yet */}
        {!btResults && deploymentMode !== 'backtest' && (
          <div className="p-4 rounded-lg bg-blue-50 border border-blue-100">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-blue-800">Backtest Recommendation</p>
                <p className="text-sm text-blue-700 mt-1">
                  It's recommended to run a backtest before deploying your strategy to validate its performance. Go back to the Testing step to run a backtest.
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2"
                  onClick={() => {
                    setDeploymentMode('backtest');
                    setCurrentStepIndex(steps.findIndex(step => step.id === 'testing'));
                  }}
                >
                  <BarChart2 className="h-4 w-4 mr-1.5" />
                  Go to Backtest
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Test Authentication Button for Debugging */}
        <div className="flex gap-2 mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              console.log('🔧 Testing Execution Timing Capture...');
              console.log('Current formData.executionTiming:', formData.executionTiming);
              const testJSON = prepareAndLogStrategyJSON('deploy');
              console.log('Execution timing in JSON:', testJSON.execution_timing);
              
              // Show user-friendly summary
              const signalFreq = formData.executionTiming?.signalFrequency || 'daily_close';
              const monitorFreq = formData.executionTiming?.monitorFrequency || '5min';
              const rthOnly = formData.executionTiming?.executeRthOnly ? 'Yes' : 'No';
              
              alert(`✅ Execution Timing Validation:
Signal Frequency: ${signalFreq}
Monitor Frequency: ${monitorFreq}  
RTH Only: ${rthOnly}
Pre/Post Market: ${formData.executionTiming?.allowPrePostMarket ? 'Yes' : 'No'}

This data will be included in your strategy deployment. Check console for full JSON.`);
            }}
          >
            🔧 Test Execution Timing
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                console.log('Testing authentication methods...');
                const authResults = await deploymentService.testAuthentication();
                console.log('Authentication test results:', authResults);
                alert(`Authentication test completed. Check console for results. Found ${authResults.filter(r => r.success).length} working methods.`);
              } catch (error) {
                console.error('Authentication test failed:', error);
                alert(`Authentication test failed: ${error.message}`);
              }
            }}
          >
            🔐 Test Backend Auth
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                console.log('Testing direct backend access...');
                const result = await deploymentService.testDirectBackend();
                console.log('Direct backend test result:', result);
                alert('Direct backend test successful! Check console for details.');
              } catch (error) {
                console.error('Direct backend test failed:', error);
                alert(`Direct backend test failed: ${error.message}`);
              }
            }}
          >
            🌐 Test Direct Backend
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                console.log('Testing simple deployment...');
                const deployJSON = prepareAndLogStrategyJSON('deploy');
                const result = await deploymentService.deployStrategySimple(deployJSON);
                console.log('Simple deployment test result:', result);
                alert(`Simple deployment test: ${result.success ? 'SUCCESS' : 'FAILED'}. Check console for details.`);
              } catch (error) {
                console.error('Simple deployment test failed:', error);
                alert(`Simple deployment test failed: ${error.message}`);
              }
            }}
          >
            🚀 Test Simple Deploy
          </Button>
          <span className="text-sm text-yellow-700 flex items-center">
            ⚠️ Debug tools - remove in production
          </span>
        </div>
      </div>
    );
  };

  return (
    <ErrorBoundary>
      <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
        <DialogContent className="sm:max-w-[900px] max-h-[95vh] p-0 overflow-auto bg-white border-2 border-gray-200 shadow-2xl">
          {/* Enhanced Header with Premium Design */}
          <div className="relative z-10 bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50 border-b border-gray-200">
            <div className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                  <BriefcaseBusiness className="h-8 w-8 text-white" />
                </div>
                <div>
                  <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    {initialData ? 'Edit Strategy' : 'Create New Strategy'}
                  </DialogTitle>
                  <p className="text-sm text-blue-600 font-medium mt-1">
                    Build and deploy your intelligent trading strategy
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Enhanced Progress Indicator */}
          <div className="relative z-10 bg-gray-50 border-b border-gray-200 px-6 py-4">
            <div className="space-y-4">
              {/* Desktop Progress Indicator */}
              <div className="hidden sm:flex items-center justify-between">
                {steps.map((step, index) => (
                  <div
                    key={step.id}
                    className={cn(
                      "flex items-center",
                      index !== steps.length - 1 && "flex-1"
                    )}
                  >
                    <div
                      className={cn(
                        "flex h-12 w-12 items-center justify-center rounded-xl border-2 shadow-md transition-all duration-300",
                        currentStepIndex > index
                          ? "border-blue-500 bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg"
                          : currentStepIndex === index
                          ? "border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-600 shadow-lg scale-105"
                          : "border-gray-300 bg-white text-gray-400 hover:border-gray-400 transition-colors"
                      )}
                    >
                      {currentStepIndex > index ? (
                        <Check className="h-6 w-6" />
                      ) : (
                        step.icon
                      )}
                    </div>
                    {index !== steps.length - 1 && (
                      <div className="flex-1 px-4">
                        <div
                          className={cn(
                            "h-1 rounded-full transition-all duration-500",
                            currentStepIndex > index
                              ? "bg-gradient-to-r from-blue-500 to-indigo-500"
                              : "bg-gray-200"
                          )}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Mobile Progress Indicator */}
              <div className="sm:hidden">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-medium text-blue-600">
                    Step {currentStepIndex + 1} of {steps.length}
                  </div>
                  <div className="text-sm font-bold text-gray-700">{currentStep.title}</div>
                </div>
                <div className="relative">
                  <Progress value={progress} className="h-3 bg-gray-200 [&>div]:bg-gradient-to-r [&>div]:from-blue-500 [&>div]:to-indigo-500" />
                </div>
              </div>
              
              {/* Step Title and Description */}
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-800">{currentStep.title}</h3>
                <p className="text-sm text-gray-600 mt-1">{currentStep.description}</p>
              </div>
            </div>
          </div>
          
          {/* Enhanced Content Area */}
          <div className="relative z-10 flex-1 overflow-hidden bg-white">
            <ScrollArea className="h-[500px] px-6 py-6">
              <div className="space-y-6">
                {/* Clean content area without extra background cards */}
                <div key={currentStep.id}>
                  {renderStepContent()}
                </div>
              </div>
            </ScrollArea>
          </div>
          
          {/* Enhanced Footer */}
          <div className="relative z-10 bg-gray-50 border-t border-gray-200">
            <div className="flex justify-between items-center p-6">
              {currentStepIndex > 0 ? (
                <Button 
                  variant="outline" 
                  onClick={goToPreviousStep}
                  className="bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
                >
                  <ChevronLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
              ) : (
                <div></div>
              )}
              
              <div className="flex items-center gap-3">
                <DialogClose asChild>
                  <Button 
                    variant="ghost" 
                    className="hover:bg-gray-100 transition-all duration-200"
                  >
                    Cancel
                  </Button>
                </DialogClose>
                
                {currentStepIndex < steps.length - 1 ? (
                  <Button 
                    onClick={goToNextStep}
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 px-6"
                  >
                    Continue
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                ) : (
                  <Button 
                    onClick={handleStrategySubmit}
                    disabled={isSubmitting}
                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 px-8"
                  >
                    {isSubmitting ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Deploying...
                      </>
                    ) : (
                      <>
                        {initialData ? 'Update Strategy' : 'Deploy Strategy'}
                        <Zap className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Backtest History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-6xl h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Backtest History</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <div className="bg-blue-50 p-3 rounded-lg mb-4 border border-blue-100">
              <h3 className="text-sm font-medium text-blue-800 mb-1">Your Backtest History</h3>
              <p className="text-xs text-blue-600">View all your previous backtests, analyze performance trends, and compare different strategies.</p>
            </div>
            <BacktestHistoryView />
          </div>
        </DialogContent>
      </Dialog>
    </ErrorBoundary>
  );
};

// Helper to format numbers consistently in the UI without extra multiplication
const formatMetric = (value: number | string | undefined, isPercent = true): string => {
  if (value === undefined || value === null) return '0.00';
  
  // Convert to number to ensure consistent handling
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  // For percentages, if the value is already a percentage (>= 1), don't multiply
  // This handles the case where the backend returns 50 for 50% instead of 0.5
  if (isPercent && numValue >= 1 && numValue <= 100) {
    return numValue.toFixed(2);
  }
  
  // Return formatted value
  return isPercent ? (numValue * 100).toFixed(2) : numValue.toString();
};